import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../utils/cn';
import { authCardEnter } from './authMotion';
import { useAuthCardDepth } from './useAuthCardDepth';

interface AuthCardProps {
  children: React.ReactNode;
  className?: string;
}

export const AuthCard: React.FC<AuthCardProps> = ({ children, className }) => {
  const reduceMotion = useReducedMotion();
  const { ref, transform, onPointerMove, onPointerLeave, reduceMotion: depthOff } =
    useAuthCardDepth();

  return (
    <motion.div
      ref={ref}
      className={cn('w-full', className)}
      {...(depthOff ? {} : { style: { transform } })}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      variants={authCardEnter}
      initial={reduceMotion ? false : 'hidden'}
      animate="visible"
    >
      <motion.div
        className={cn(
          'relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6',
          !depthOff && 'transition-shadow duration-300 hover:shadow-[0_28px_60px_-28px_rgba(15,23,42,0.14)]'
        )}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent"
          aria-hidden
        />
        <motion.div
          className="pointer-events-none absolute -left-1/4 top-0 h-32 w-1/2 bg-gradient-to-br from-blue-50/50 to-transparent"
          aria-hidden
        />
        {children}
      </motion.div>
    </motion.div>
  );
};

interface AuthCardHeaderProps {
  title: string;
  subtitle?: string;
  className?: string;
}

export const AuthCardHeader: React.FC<AuthCardHeaderProps> = ({ title, subtitle, className }) => (
  <header className={cn('relative mb-5', className)}>
    <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-[1.65rem]">{title}</h1>
    {subtitle ? <p className="mt-1.5 text-sm leading-snug text-slate-600">{subtitle}</p> : null}
  </header>
);

export const AuthTrustFooter: React.FC = () => (
  <p className="mt-5 border-t border-slate-100 pt-4 text-center text-[11px] leading-snug text-slate-500">
    Encrypted in transit · SOC-ready infrastructure · Privacy-first by design
  </p>
);
